/**
 * ============================================================================
 * AGGREGATION CORE — the single implementation of WBR aggregation semantics.
 * ============================================================================
 *
 * LAYER: pure domain logic (no React, no Redux, no transport). Both selector
 * modules (tableSelectors for the executive-summary tables, metricsSelectors
 * for the home-page cards) MUST derive from this module — the QuickSight
 * parity rules live here and ONLY here:
 *
 *   percentage → 100 × SUM(numeratorValue) / SUM(denominatorValue) per week
 *                across DCRs. The API sends both operand values on every
 *                record, so this is exact — no mean-of-rates approximation.
 *                Records missing operand values fall back to a value-weighted
 *                mean of the pre-computed per-DCR rates (documented
 *                degradation, applied per week, never mixed with SUM/SUM).
 *   volume / count → SUM(value) across DCRs.
 *
 * Metrics are identified by `metricKey`; presentation (name, goal, type,
 * operand identity) comes from the getMetricDefinitions catalog, never from
 * the per-record entries.
 */

import {
  DcrRecord,
  MetricDefinition,
  MetricEntry,
} from 'src/types/metricsApi.types';

export type NormalizedMetricType = 'percentage' | 'volume' | 'count';

/** metricKey → definition. Built once per response, passed into selectors. */
export type MetricCatalog = Map<string, MetricDefinition>;

export function buildMetricCatalog(
  definitions: MetricDefinition[] | undefined,
): MetricCatalog {
  const catalog: MetricCatalog = new Map();
  for (const definition of definitions ?? []) {
    catalog.set(definition.metricKey, definition);
  }
  return catalog;
}

/** The catalog sends UPPERCASE MetricType ("PERCENTAGE"); normalize once here. */
export function normalizeMetricType(
  type: string | undefined,
): NormalizedMetricType {
  const normalized = (type ?? 'count').toLowerCase();
  return normalized === 'percentage' || normalized === 'volume'
    ? normalized
    : 'count';
}

/**
 * THE display unit for a metric's values and goal (backend spec
 * hilt-wbr-api-serving 6.2.1): the catalog's `unit` when present ("%",
 * "days", "items"), else the type-derived fallback the contract documents —
 * PERCENTAGE implies "%", others unitless. The single resolution every
 * surface (tables, cards, exports) formats from, so units can never drift
 * between them.
 */
export function metricUnit(
  definition: Pick<MetricDefinition, 'unit' | 'type'>,
): string {
  if (definition.unit) return definition.unit;
  return normalizeMetricType(definition.type) === 'percentage' ? '%' : '';
}

/**
 * Split a Canonical Week Token into a stable column key and a display label:
 * "2026 Week 32" → { key: "2026Week32", label: "2026 Week 32" }.
 */
export function parseReportingPeriod(reportingPeriod: string): {
  key: string;
  label: string;
} {
  // Defensive: a malformed period must degrade to an unlabelled column, never
  // throw and take the whole table down with it.
  if (!reportingPeriod) return { key: '', label: '' };
  const match = reportingPeriod.match(/^(\d{4})[\s-]*(?:WK|Week)\s*(\d+)$/i);
  if (!match) return { key: reportingPeriod, label: reportingPeriod };
  return {
    key: `${match[1]}Week${match[2]}`,
    label: `${match[1]} Week ${parseInt(match[2], 10)}`,
  };
}

/**
 * parseFloat that treats absent/blank/NaN uniformly as "no value". Exported so
 * per-DCR consumers (deepDiveSelectors) parse a raw `value` by the same rule
 * the aggregators use.
 */
export function toNumber(value: string | undefined): number | null {
  if (value === undefined || value === null || value === '') return null;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Per-DCR `metricKey → entry` index, built once per DCR and cached by object
 * identity. getMetrics records are stable objects in the RTK cache, so the index
 * survives re-pivots (filter / window changes) — turning each metric lookup from
 * a linear scan of the DCR's metric entries into O(1), which is what the tables
 * do on every DCR × metric × week × section. First-wins on a duplicate key, to
 * match the previous `Array.find`. The WeakMap releases an index when its DCR is
 * garbage-collected (cache eviction), so it cannot leak.
 */
const entryIndexByDcr = new WeakMap<DcrRecord, Map<string, MetricEntry>>();

function entryIndexOf(dcr: DcrRecord): Map<string, MetricEntry> {
  let index = entryIndexByDcr.get(dcr);
  if (index === undefined) {
    index = new Map();
    for (const entry of dcr.metrics) {
      if (!index.has(entry.metricKey)) index.set(entry.metricKey, entry);
    }
    entryIndexByDcr.set(dcr, index);
  }
  return index;
}

/** The DCR's entry for a metric, or undefined when absent (O(1); see entryIndexOf). */
export function findEntry(
  dcr: DcrRecord,
  metricKey: string,
): MetricEntry | undefined {
  return entryIndexOf(dcr).get(metricKey);
}

export interface MetricAccumulation {
  /** SUM(value) across DCRs (volume/count path). */
  sum: number;
  /** SUM of numerator values (percentage path). */
  sumNum: number;
  /** SUM of denominator values (percentage path). */
  sumDen: number;
  isPercentage: boolean;
  /** False when no DCR in the slice carries the metric. */
  seen: boolean;
}

/**
 * Accumulate one metric across a set of DCRs (one week's slice). Raw sums —
 * callers decide how to finalize (rate, attainment, etc.).
 */
export function accumulateMetric(
  dcrs: DcrRecord[],
  metricKey: string,
  catalog: MetricCatalog,
): MetricAccumulation {
  const acc: MetricAccumulation = {
    sum: 0,
    sumNum: 0,
    sumDen: 0,
    isPercentage: false,
    seen: false,
  };
  const isPercentage =
    normalizeMetricType(catalog.get(metricKey)?.type) === 'percentage';

  // Pass 1 (percentage only): does ANY entry in the slice carry operand
  // values? If so the slice aggregates like the QuickSight field: each operand
  // is summed independently over the values that exist, and a missing operand
  // simply contributes nothing (in QuickSight an absent row is NULL, skipped
  // by sum()). A DCR with demand but no delivery therefore drags the rate
  // down — that is the honest network rate. Only a slice with NO operand data
  // at all falls back to the pre-computed per-record rates (mean of rates);
  // the two are never mixed, since rate points and raw units share no scale.
  let sliceHasOperands = false;
  if (isPercentage) {
    for (const dcr of dcrs) {
      const entry = findEntry(dcr, metricKey);
      if (!entry) continue;
      if (
        toNumber(entry.numeratorValue) !== null ||
        toNumber(entry.denominatorValue) !== null
      ) {
        sliceHasOperands = true;
        break;
      }
    }
  }

  for (const dcr of dcrs) {
    const entry = findEntry(dcr, metricKey);
    if (!entry) continue;

    if (isPercentage) {
      const numerator = toNumber(entry.numeratorValue);
      const denominator = toNumber(entry.denominatorValue);
      const rate = toNumber(entry.value);
      // A record with neither operands nor a rate carries no information for
      // this metric — it must not count as "seen" (that would turn an absent
      // metric into a 0% row).
      if (numerator === null && denominator === null && rate === null) continue;
      acc.seen = true;
      acc.isPercentage = true;
      if (sliceHasOperands) {
        // QuickSight semantics: independent sums, absent values contribute
        // nothing to their side.
        acc.sumNum += numerator ?? 0;
        acc.sumDen += denominator ?? 0;
      } else if (rate !== null) {
        // Pre-computed rate in `value`; weight 1 per DCR (mean of rates).
        acc.sumNum += rate;
        acc.sumDen += 100;
      }
      continue;
    }

    const value = toNumber(entry.value);
    if (value === null) continue;
    acc.seen = true;
    acc.sum += value;
  }

  return acc;
}

/**
 * THE rate finalization — the only place the zero-denominator rule lives.
 * Every consumer (aggregateMetric, metricsSelectors attainment) must use this
 * instead of re-deriving sumNum/sumDen math.
 *
 * QuickSight parity (quicksightStack.ts calculated fields): 100 × sum/sum,
 * NULL on a zero denominator (blank cell, not 0%), no display-side cap. Rates
 * still never exceed 100 in practice because the backend clamps the network
 * OTD numerator in the data — SUM(LEAST(delivered, committed)) — which is the
 * dashboard's actual capping mechanism.
 */
export function finalizeRate(sumNum: number, sumDen: number): number | null {
  return sumDen > 0 ? (sumNum / sumDen) * 100 : null;
}

/** The per-DCR numeric values present for a metric across a week's slice. */
export function numericValues(dcrs: DcrRecord[], metricKey: string): number[] {
  const values: number[] = [];
  for (const dcr of dcrs) {
    const entry = findEntry(dcr, metricKey);
    if (!entry) continue;
    const value = toNumber(entry.value);
    if (value !== null) values.push(value);
  }
  return values;
}

/** Arithmetic mean, or null for an empty set (AVERAGE aggregation). */
export function meanOf(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/**
 * Redshift PERCENTILE_DISC parity: the value at the smallest cumulative
 * fraction (i / N) ≥ p. Nearest-rank over the discrete data — always a real
 * observed value, never interpolated (matches the backend, which uses
 * PERCENTILE_DISC). `percentile` is 0–100. Null for an empty set.
 */
export function percentileDisc(
  values: number[],
  percentile: number,
): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const p = Math.min(100, Math.max(0, percentile)) / 100;
  const n = sorted.length;
  for (let i = 1; i <= n; i++) {
    if (i / n >= p) return sorted[i - 1];
  }
  return sorted[n - 1];
}

export interface AggregatedMetric {
  /** null = metric absent from every DCR in the slice. */
  value: number | null;
  isRate: boolean;
}

/** Finalized aggregate: rate (uncapped; null on zero denominator) or plain sum. */
export function aggregateMetric(
  dcrs: DcrRecord[],
  metricKey: string,
  catalog: MetricCatalog,
): AggregatedMetric {
  const acc = accumulateMetric(dcrs, metricKey, catalog);
  if (!acc.seen) return { value: null, isRate: false };
  if (acc.isPercentage) {
    return { value: finalizeRate(acc.sumNum, acc.sumDen), isRate: true };
  }
  return { value: acc.sum, isRate: false };
}

/**
 * Per-DCR rate for the derived "% DCRs ≥ threshold" rows. The backend's
 * documented per-DCR formula is LEAST(100, 100 × num/den) (seed_metric_definition),
 * so the cap applies HERE, unlike network rates where the clamp is baked into
 * the numerator data.
 */
export function perDcrRate(entry: MetricEntry): number | null {
  const numerator = toNumber(entry.numeratorValue);
  const denominator = toNumber(entry.denominatorValue);
  if (numerator !== null && denominator !== null) {
    return denominator > 0
      ? Math.min(100, (numerator / denominator) * 100)
      : null;
  }
  // Cap the pre-computed value fallback too (same LEAST(100, …) rule): otherwise
  // a carrier shipping a raw value > 100 would render a cell above the capped
  // Grand Total, so a visible cell could exceed the total beneath it.
  const fallback = toNumber(entry.value);
  return fallback === null ? null : Math.min(100, fallback);
}
